import { type ASTExpr } from '../lscript/ast';
import { Opaque } from '../utils/Opaque';

/*
 * Definition of a conversion between two units.  The specific units are
 * not specified here, but are instead specified in the registry.
 */
export interface Conversion {
  fromBase: (input: ASTExpr) => ASTExpr; // Conversion to the unit from the base unit
  toBase: (input: ASTExpr) => ASTExpr; // Conversion from the unit to the base unit
}

/*
 * A unit is a map from atomic units to exponents.  For example, the
 * unit "meter per second" is represented as the map
 * { meter: 1, second: -1 }.
 */
export type AtomicUnit = Opaque<string, 'AtomicUnit'>;
export type Unit = Map<AtomicUnit, number>;

/*
 * A convenience type for specifying units.  This can be either a unit string,
 * or an object with the same atomic unit/exponent pairs that will go into the map.
 */
export type UnitSpec = Record<string, number> | string;

/**
 * A non-exhaustive list of unit groups. Each group represents a set of units
 * that are commensurable with one another. Not every unit is represented here,
 * as it's possible to write arbitrarily complex compound units.
 *
 * These groups exist because:
 *
 * 1. BI unit conversion uses them to determine which units within a field are
 *    commensurable (see convertUnitMiddleware.ts).
 * 2. Reference data for Supply Chain LCAs is organized by unit group (see
 *    SupplierEmissionsFactorsService.ts).
 *
 * @deprecated Unless needed for one of the above cases, do not add new unit
 * groups or rely on these groups in new code. Instead, check commensurability
 * directly using `isUnitCommensurable`.
 */
export const UNIT_GROUPS = [
  'area',
  'length',
  'mass',
  'volume',
  'time',
  'calendar',
  'fuelEconomy',
  'energyEconomy',
  'temperature',
  'textile',
  'energy',
  'power',
  'count',
  'currency',
  'information',
  'force',
  'carbon',
  'storage',
  'transport',
] as const;

/**
 * A group of units that are commensurable with one another. Not every unit
 * is represented by one of these named unit groups.
 *
 * @deprecated See UNIT_GROUPS for more information.
 */
export type UnitGroup = (typeof UNIT_GROUPS)[number];
